import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, nextTick, getCurrentInstance, onBeforeUnmount } from 'vue';
import ClientProjectsTableWrapper from '@/components/views/ClientEdit/ClientProjectsTableWrapper.vue';
import { ClientTabs } from '@/enums/components/EditClient/ClientTabs';
import CustomerInformation from '@/components/global/client/CustomerInformation.vue';
import ContactInformation from '@/components/global/client/ContactInformation.vue';
import DiscountInformation from '@/components/global/client/DiscountInformation.vue';
import BookingInformation from '@/components/global/client/BookingInformation.vue';
import PaymentsInformation from '@/components/global/client/PaymentsInformation.vue';
import DeliveryInformation from '@/components/global/client/DeliveryInformation.vue';
import StatusInformation from '@/components/global/client/StatusInformation.vue';
import Currency from '@/models/Currency';
import PaymentType from '@/models/PaymentType';
import BankAccount from '@/models/BankAccount';
import Country from '@/models/Country';
import PriceType from '@/models/PriceType';
import PriceListRegion from '@/models/PriceListRegion';
import ContactPerson from '@/models/ContactPerson';
import ContactConfig from '@/models/interfaces/Contact';
import AddressConfig from '@/models/interfaces/Address';
import Address from '@/models/Address';
import Discount from '@/models/Discount';
import Client from '@/models/Client';
import { AvailableClientFields } from '@/interfaces/components/clients/AvailableClientFields';
import ClientTypeRepository from '@/repositories/ClientTypeRepository';
import PaymentTypeRepository from '@/repositories/PaymentTypeRepository';
import ProductGroup from '@/models/ProductGroup';
import { EventBus } from '@/helpers/EventBusHelper';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import { PopupEvents } from '@/enums/global/PopupEvents';
import CMSUser from '@/models/CMSUser';
import ClientType from '@/models/ClientType';
import { UserRightsEnum } from '@/enums/global/UserRights';
import { ErrorOptions } from '@/interfaces/ErrorOptions';
import { DiscountTableEntry } from '@/interfaces/components/clients/DiscountTableEntry';
import { webpageValidationRegex, emailValidationRegex } from '@/regex/formRegex';
import LeadOrigin from '@/models/LeadOrigin';
import AddressType from '@/models/AddressType';
import { UserRepository } from '@/repositories/UserRepository';
import ClientNotes from '@/components/global/client/ClientNotes.vue';
import UserGroupRepository from '@/repositories/UserGroupRepository';
import AddressTypeRepository from '@/repositories/AddressTypeRepository';
import store from '@/store';
import CapitalConnection from '@/models/CapitalConnection';
import AccountingInfoRepository from '@/repositories/AccountingInfoRepository';
import GeneralBusinessPostingGroup from '@/models/GeneralBusinessPostingGroup';
import CustomerPostingGroup from '@/models/CustomerPostingGroup';
import NonResidentFinancialSector from '@/models/NonResidentFinancialSector';
import FinancialSector from '@/models/FinancialSector';
import PaymentTerms from '@/models/PaymentTerms';
import ShipmentMethod from '@/models/ShipmentMethod';
import TransactionType from '@/models/TransactionType';
import TransportationType from '@/models/TransportationType';
import ControlType from '@/models/ControlType';
import DeliveryRegion from '@/models/DeliveryRegion';

interface Props {
    clientId: string;
    client: Client;
    formData: any;
    isItalyUG: boolean;
    canAccessLogistics: boolean;
    isReadOnly?: boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'EditClientTabs',
  props: {
    clientId: null,
    client: null,
    formData: null,
    isItalyUG: { type: Boolean },
    canAccessLogistics: { type: Boolean },
    isReadOnly: { type: Boolean }
  },
  emits: ["clientUpdated", "startLoading", "stopLoading"],
  setup(__props: any, { emit }: { emit: ({
    (e: 'clientUpdated'): void;
    (e: 'startLoading'): void;
    (e: 'stopLoading'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    clientId: string;
    client: Client;
    formData: any;
    isItalyUG: boolean;
    canAccessLogistics: boolean;
    isReadOnly?: boolean;
};

const { proxy } = getCurrentInstance()!;





const emailValidationOverride = ref<boolean>(false);
const emailValidationOverrideValue = ref<boolean>(false);
const isTabLoading = ref(false);
const deletedDiscountIds = ref<string[]>([]);
const validationErrors = ref<string[]>([]);
const refErrors = ref<any[]>([]);
const isEditingDiscounts = ref(false);
const activeTab = ref('Customer');
const contactErrors = ref<any[]>([]);
const customerinfo = ref<any>(null);
const clientTabs = ref<any>(null);

const mailFormat = emailValidationRegex;

const userRights = computed(() => {
    return store.getters['jwtData/userRights'];
});

const canUserEditDiscounts = computed(() => {
    return userRights.value.includes(UserRightsEnum.EDIT_DISCOUNTS);
});

const isBasicClient = computed(() => {
    return props.client?.isBasic ?? false;
});

const canEditAccountingInfo = computed(() => {
    const rights = store.getters['jwtData/userRights'];
    return rights.includes(UserRightsEnum.EDIT_CLIENT_ACCOUNTING_INFO);
});

const proxyClientTabs = computed(() => {
    return {
        CUSTOMER: ClientTabs.CUSTOMER,
        CONTACT: ClientTabs.CONTACT,
        PROJECTS: ClientTabs.PROJECTS,
        DISCOUNTS: ClientTabs.DISCOUNTS,
        FAMILY: ClientTabs.FAMILY,
        NOTES: ClientTabs.NOTES,
        BOOKING: ClientTabs.BOOKING,
        PAYMENTS: ClientTabs.PAYMENTS,
        DELIVERY: ClientTabs.DELIVERY,
        STATUS: ClientTabs.STATUS,
    };
});

const defaultCountryId = computed(() => {
    const currentlySelectedCountry = props.formData.clientAddresses[0]?.country;

    if (currentlySelectedCountry) {
        return currentlySelectedCountry;
    }

    let defaultCountry;

    const currentUserGroup = UserGroupRepository.getById(String(store.getters['jwtData/currentUserGroup']));

    if (currentUserGroup?.client) {
        defaultCountry = currentUserGroup.client?.address?.country.id;
    }

    defaultCountry = currentUserGroup?.clientCountryId?.id;

    return defaultCountry;
});

const clientTypes = computed(() => {
    return ClientTypeRepository.getAll();
});

const paymentTypes = computed(() => {
    return PaymentTypeRepository.getAllWithArchived();
});

const addressTypes = computed(() => {
    return AddressTypeRepository.getAll();
});

const capitalConnections = computed(() => {
    return AccountingInfoRepository.getAll(CapitalConnection);
});

const generalBusinessPostingGroups = computed(() => AccountingInfoRepository.getAll(GeneralBusinessPostingGroup));

const customerPostingGroups = computed(() => AccountingInfoRepository.getAll(CustomerPostingGroup));

const nonResidentFinancialSectors = computed(() => AccountingInfoRepository.getAll(NonResidentFinancialSector));

const financialSectors = computed(() => AccountingInfoRepository.getAll(FinancialSector));

const paymentTerms = computed(() => AccountingInfoRepository.getAll(PaymentTerms));

const shipmentMethods = computed(() => AccountingInfoRepository.getAll(ShipmentMethod));

const transactionTypes = computed(() => AccountingInfoRepository.getAll(TransactionType));

const transportationTypes = computed(() => AccountingInfoRepository.getAll(TransportationType));

const controlTypes = computed(() => AccountingInfoRepository.getAll(ControlType));

const displayCustomerType = computed(() => {
    return proxy!.$t(props.formData.isCompany ? 'Company' : 'Natural person');
});

const contactTab = computed(() => {
    return props.formData.isCompany ? proxyClientTabs.value.CONTACT : proxyClientTabs.value.FAMILY;
});

const isCurrentUserTechnologist = computed(() => {
    return UserRepository.isCurrentUserTechnologist();
});

const isClientProtected = computed(() => {
    return props.client?.isBasic;
});

watch(
    () => props.client,
    async () => {
        await nextTick();

        const clientExists = props.client && Object.keys(props.client as { [key: string]: any }).length;
        isTabLoading.value = !clientExists;
    },
    { immediate: true },
);

function setFieldsBorder(
    arr: string[],
    addressErrors: Array<{ ref: string; addressIndex: number }>,
    clear: boolean = false,
) {
    let customerInfoRef = customerinfo.value as any;

    if (Array.isArray(customerInfoRef)) {
        customerInfoRef = customerInfoRef[0];
    }

    if (!customerInfoRef) {
        return;
    }

    const borderStyle = clear ? '0.0625rem solid #d9d9d9' : '0.0625rem solid red';

    arr.forEach((item: string) => {
        if (item === 'phone') {
            const phoneWrapper = document.getElementById('phone-number-wrapper');
            if (phoneWrapper) {
                phoneWrapper.style.border = borderStyle;
            }
            return;
        }

        // Handle 'name' field which is inside customerTypeSection
        if (item === 'name') {
            const customerTypeSection = customerInfoRef.$refs.customerTypeSection as any;
            if (customerTypeSection) {
                const el = customerTypeSection.nameInput || customerTypeSection.$refs?.nameInput;
                if (el && el.$el) {
                    el.$el.style.border = borderStyle;
                } else if (el && el.style) {
                    el.style.border = borderStyle;
                }
            }
            return;
        }

        // Handle 'tax' field which is inside taxesPaymentSection
        if (item === 'tax') {
            const taxesPaymentSection = customerInfoRef.$refs.taxesPaymentSection as any;
            if (taxesPaymentSection) {
                const el = taxesPaymentSection.tax || taxesPaymentSection.$refs?.tax;
                if (el && el.$el) {
                    el.$el.style.border = borderStyle;
                } else if (el && el.style) {
                    el.style.border = borderStyle;
                }
            }
            return;
        }

        const el = customerInfoRef.$refs[item] as any;

        if (el && el.$el) {
            el.$el.style.border = borderStyle;
        } else if (el && el.style) {
            el.style.border = borderStyle;
        }
    });

    addressErrors.forEach((item) => {
        const addressFormRef = `addressForm-${item.addressIndex}`;
        let addressForm = customerInfoRef.$refs[addressFormRef] as any;

        // Handle array refs (Vue wraps v-for refs in arrays)
        if (Array.isArray(addressForm)) {
            addressForm = addressForm[0];
        }

        if (!addressForm) {
            return;
        }

        // Try to find the ref directly on addressForm
        let el = addressForm.$refs[item.ref] as any;

        // If not found, try in addressLocationFields child component
        if (!el && addressForm.$refs.addressLocationFields) {
            const locationFields = addressForm.$refs.addressLocationFields as any;
            if (locationFields[item.ref]) {
                el = locationFields[item.ref];
            } else if (locationFields.$refs && locationFields.$refs[item.ref]) {
                el = locationFields.$refs[item.ref];
            }
        }

        // Handle Vue 3 ref objects
        if (el && el.value && el.value.$el) {
            el = el.value;
        }

        if (el && el.$el) {
            el.$el.style.border = borderStyle;
        } else if (el && el.style) {
            el.style.border = borderStyle;
        }
    });
}

function confirmFieldsAreValid() {
    validationErrors.value = [];
    refErrors.value = [];
    const addressErrors: Array<{ ref: string; addressIndex: number }> = [];

    let flag = true;
    let customerIsValid = true;
    let contactInfoIsValid = true;

    if (!props.formData.name) {
        flag = false;
        customerIsValid = false;
        validationErrors.value.push(proxy!.$t('Polje ime je obavezno') as string);
        refErrors.value.push('name');
    }

    // Validate clientAddresses array
    if (!props.formData.clientAddresses || props.formData.clientAddresses.length === 0) {
        flag = false;
        customerIsValid = false;
        validationErrors.value.push(proxy!.$t('At least one address is required') as string);
    } else {
        props.formData.clientAddresses.forEach((address: any, index: number) => {
            if (!address?.streetNameAndNumber) {
                flag = false;
                customerIsValid = false;
                validationErrors.value.push(proxy!.$t('Polje naziv ulice i broj je obavezno') as string);
                addressErrors.push({ ref: 'addressStreetNameAndNumber', addressIndex: index });
            }

            if (!address?.poNumber) {
                flag = false;
                customerIsValid = false;
                validationErrors.value.push(proxy!.$t('Polje poštanski broj je obavezno') as string);
                addressErrors.push({ ref: 'addressPoNumber', addressIndex: index });
            }

            if (!address?.city) {
                flag = false;
                customerIsValid = false;
                validationErrors.value.push(proxy!.$t('Polje grad je obavezno') as string);
                addressErrors.push({ ref: 'addressCity', addressIndex: index });
            }

            if (!address?.country) {
                flag = false;
                customerIsValid = false;
                validationErrors.value.push(proxy!.$t('Polje država je obavezno') as string);
                addressErrors.push({ ref: 'country', addressIndex: index });
            }
        });
    }

    let missingContactInfo = false;
    let missingAddressInfo = false;

    const contactErrorsLocal: any = [];
    props.formData.contacts.forEach((contact: any, i: number) => {
        const isContactValid = !!contact.name && !!contact.email && !!contact.phone;

        const isAddressValid =
            !!contact.address?.streetNameAndNumber &&
            !!contact.address?.poNumber &&
            !!contact.address?.city &&
            !!contact.address?.country;

        if (!isContactValid) {
            missingContactInfo = true;
        }

        if (!isAddressValid) {
            missingAddressInfo = true;
        }

        contactErrorsLocal.push({ index: i, errors: checkContactFieldsForErrors(contact) });
    });

    contactErrors.value = contactErrorsLocal;

    if (missingContactInfo) {
        flag = false;
        contactInfoIsValid = false;
        validationErrors.value.push(proxy!.$t('Polja imena, emaila i telefona su obavezna') as string);
    }

    if (missingAddressInfo) {
        flag = false;
        contactInfoIsValid = false;
        validationErrors.value.push(proxy!.$t('Polja adrese kontakt osobe su obavezna') as string);
    }

    // @todo check every contact
    if (!props.formData.contacts.length) {
        flag = false;
        contactInfoIsValid = false;
        validationErrors.value.push(proxy!.$t('Nedostaje kontakt osoba') as string);
        refErrors.value.push('contacts');
    }

    if (props.formData?.isCompany) {
        const webpageValid = validateWebpage();

        if (!webpageValid) {
            flag = false;
            customerIsValid = false;
            validationErrors.value.push(proxy!.$t('Invalid webpage!') as string);
            refErrors.value.push('webpage');
        }
    }

    if (props.formData.isCompany || props.isItalyUG) {
        if (!props.formData.taxId) {
            flag = false;
            customerIsValid = false;
            validationErrors.value.push(proxy!.$t('Polje porezni id broj je obavezno') as string);
            refErrors.value.push('tax');
        }
    }

    // Clear previous borders on all fields
    const allClearableRefs = ['name', 'phone', 'tax', 'contacts'];
    const allAddressRefs = ['addressStreetNameAndNumber', 'addressPoNumber', 'addressCity', 'country'];
    const allAddressErrors: Array<{ ref: string; addressIndex: number }> = [];
    if (props.formData.clientAddresses) {
        props.formData.clientAddresses.forEach((_: any, index: number) => {
            allAddressRefs.forEach((refName) => {
                allAddressErrors.push({ ref: refName, addressIndex: index });
            });
        });
    }
    setFieldsBorder(allClearableRefs, allAddressErrors, true);

    // Set red borders on invalid fields
    if (!flag) {
        setFieldsBorder(refErrors.value, addressErrors, false);
    }

    switchToCorrectTab(customerIsValid, contactInfoIsValid);

    return flag;
}

function validateWebpage() {
    const input = props.formData.webpage;

    if (!input) {
        return true;
    }

    if (!webpageValidationRegex.test(input)) {
        return false;
    }

    return true;
}

function checkContactFieldsForErrors(contact: any) {
    const errorState: any = {};

    if (!contact.email) {
        errorState.email = true;
    }

    if (!contact.phone) {
        errorState.phone = true;
    }

    if (!contact.name) {
        errorState.name = true;
    }

    if (contact.useDefaultAddress) {
        return errorState;
    }

    if (!contact.address?.city) {
        errorState.city = true;
    }

    if (!contact.address?.country) {
        errorState.country = true;
    }

    if (!contact.address?.poNumber) {
        errorState.poNumber = true;
    }

    if (!contact.address?.streetNameAndNumber) {
        errorState.streetNameAndNumber = true;
    }

    return errorState;
}

function switchToCorrectTab(customerIsValid: boolean, contactInfoIsValid: boolean) {
    let contactTabName = '';

    if (props.formData.isCompany) {
        contactTabName = proxyClientTabs.value.CONTACT;
    } else {
        contactTabName = proxyClientTabs.value.FAMILY;
    }

    switch (activeTab.value) {
        case 'Customer':
            if (!customerIsValid) {
                return;
            }

            if (!contactInfoIsValid) {
                activeTab.value = contactTabName;
            }
            break;
        case contactTabName:
            if (!contactInfoIsValid) {
                return;
            }

            if (!customerIsValid) {
                activeTab.value = 'Customer';
            }
        default:
            if (!customerIsValid) {
                activeTab.value = 'Customer';
            }

            if (!contactInfoIsValid) {
                activeTab.value = contactTabName;
            }
    }
}

function openDuplicateClientPopup(duplicate: Client) {
    EventBus.$emit(EventBusEvents.openDuplicateClientPopup, {
        popupEvent: PopupEvents.openDuplicateClient,
        data: {
            client: duplicate,
        },
    });
}

async function confirmEmailsAreValid() {
    const contactEmails = props.formData.contacts.map((contact: any) => {
        return contact.email;
    });

    const emails = [...contactEmails];
    const clients: Client[] = [];

    for await (const email of emails) {
        if (email) {
            if (!email.match(mailFormat)) {
                proxy!.$notification.error({
                    message: proxy!.$t('Invalid email') as string,
                    description: '',
                });
                emit('stopLoading');

                return false;
            }

            const duplicates = await checkDuplicateEmails(email);
            const duplicate = findFirstNonselfDuplicate(duplicates);
            if (duplicate) {
                clients.push(duplicate);

                openDuplicateClientPopup(duplicate);
                emit('stopLoading');

                return false;
            }
        }
    }

    if (clients.length === 0) {
        return Promise.resolve(true);
    }
}

function findFirstNonselfDuplicate(duplicates: any) {
    if (!duplicates || !duplicates.length) {
        return false;
    }

    const validDuplicate = duplicates.find((duplicate: any) => {
        return duplicate && duplicate.id !== props.client.id;
    });

    return validDuplicate;
}

async function checkDuplicateEmails(input: string) {
    const customerName = props.formData.name;
    const customerAddress = props.formData.addressForm.streetNameAndNumber;
    const customerTaxId = props.formData.taxId;
    let clients = null;

    try {
        if (props.formData.isCompany && Array.isArray(props.formData.contacts)) {
            for (const contact of props.formData.contacts) {
                const result = await Client.validateClient(
                    contact.email,
                    contact.phone,
                    customerName,
                    customerAddress,
                    customerTaxId,
                );
                if (result && result.data.length > 0) {
                    return result.data;
                }
            }
        } else {
            clients = await Client.validateClient(
                input,
                props.formData.phone,
                customerName,
                customerAddress,
                customerTaxId,
            );
            return clients.data.length <= 0 ? null : clients.data;
        }
    } catch (e) {
        console.error('Error checking duplicates', e);
        return null;
    }

    return null;
}

function isCustomerInfoShown(tab: string) {
    return tab === ClientTabs.CUSTOMER;
}
function isContactInfoShown(tab: string) {
    return tab === ClientTabs.CONTACT;
}
function isProjectsShown(tab: string) {
    return tab === ClientTabs.PROJECTS;
}
function isDiscountsShown(tab: string) {
    return tab === ClientTabs.DISCOUNTS;
}
function updateIsEditingDiscounts(val: boolean) {
    isEditingDiscounts.value = val;
}
async function updateCachedValues() {
    EventBus.$emit(EventBusEvents.updateClientCachedData);
    await nextTick();
}
async function updateClientAndDepenencies({ sendToIngenious = false } = {}) {
    if (canUserEditDiscounts.value && isEditingDiscounts.value) {
        proxy!.$notification.error({
            message: proxy!.$t('Greška') as string,
            description: proxy!.$t(
                'You are still editing some discounts, please accept or decline changes to save.',
            ) as string,
        });
        return;
    }

    const customerInfoRef = customerinfo.value as any;
    if (customerInfoRef && typeof customerInfoRef.validateAll === 'function') {
        const warningKey = customerInfoRef.validateAll();
        if (warningKey) {
            const confirmed = await showWarningModal(warningKey);
            if (!confirmed) {
                emit('stopLoading');
                return;
            }
        }
    }

    emit('startLoading');
    formatDataForUpdate(props.formData);

    if (!confirmFieldsAreValid()) {
        proxy!.$notification.error({
            message: validationErrors.value.join('!\n'),
            description: '',
        });

        emit('stopLoading');

        return;
    }

    const emailsAreValid = await confirmEmailsAreValid();

    if (!emailsAreValid) {
        emit('stopLoading');

        return;
    }

    await updateCachedValues();

    try {
        if (canUserEditDiscounts.value) {
            await createDiscounts();
        }

        // Create addresses first because we needs ids for both client addresses and contact person addresses when updating/creating contacts
        await createClientAddresses(props.formData);

        props.formData.contactPersons = await updateContacts();
        await updateClientData(props.formData);

        if (sendToIngenious) {
            sendToIngeniousApi();
        }
    } catch (e) {
        const error = e as ErrorOptions;

        proxy!.$notification.error({
            message: proxy!.$t('Dogodila se greška') as string,
            description: error.response ? error.response.data.meta.message : error.message,
        });
        emit('stopLoading');
        return;
    }

    proxy!.$notification.success({
        message: proxy!.$t('Client has been updated!') as string,
        description: '',
        class: 'client-update-success',
    });

    emit('clientUpdated');
    emit('stopLoading');
}

async function updateClientWithDuplicatedValues() {
    const data = props.formData as AvailableClientFields;
    formatDataForUpdate(data);

    try {
        if (canUserEditDiscounts.value) {
            await createDiscounts();
        }

        await createClientAddresses(props.formData);

        props.formData.contactPersons = await updateContacts();
        await updateClientData(props.formData);
    } catch (e) {
        const error = e as ErrorOptions;

        proxy!.$notification.error({
            message: proxy!.$t('Dogodila se greška') as string,
            description: error.response ? error.response.data.meta.message : error.message,
        });
        emit('stopLoading');
        return;
    }

    proxy!.$notification.success({
        message: proxy!.$t('Client has been updated!') as string,
        description: '',
        duration: 3,
        class: 'client-update-success',
    });

    emit('clientUpdated');
    emit('stopLoading');
}

function showWarningModal(warningKey: string): Promise<boolean> {
    const warningMessages: Record<string, { title: string; content: string }> = {
        NO_ADDRESS: { title: 'Missing Address', content: 'Please add a default or document address. Proceed anyway?' },
        INCOMPLETE_POSTAL: {
            title: 'Incomplete Address',
            content: 'Street, City and Country are required for Mail/Post delivery. Proceed anyway?',
        },
        INVALID_EMAIL: {
            title: 'Invalid Email',
            content: 'A valid email is required for E-document delivery. Proceed anyway?',
        },
    };

    const warning = warningMessages[warningKey];

    return new Promise((resolve) => {
        proxy!.$confirm({
            title: proxy!.$t(warning.title),
            content: proxy!.$t(warning.content),
            okText: proxy!.$t('Da') as string,
            cancelText: proxy!.$t('Ne') as string,
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
        });
    });
}

function formatDataForUpdate(data: AvailableClientFields) {
    if (data.addressForm && !data.addressForm.country) {
        data.addressForm.country = defaultCountryId.value;
    }

    if (data.assignedUser && typeof data.assignedUser !== 'string' && data.assignedUser?.id) {
        data.assignedUser = data.assignedUser.id;
    }

    if (typeof data.tags[0] !== 'string') {
        data.tags = data.tags.map((tag: any) => tag.name);
    }

    if (data.discounts.length) {
        for (const discount of data.discounts) {
            if (discount.pc1) {
                discount.pc1 = parseFloat(discount.pc1);
            } else {
                discount.pc1 = 0;
            }

            if (discount.pc2) {
                discount.pc2 = parseFloat(discount.pc2);
            } else {
                discount.pc2 = 0;
            }
        }
    }

    if (props.formData.clientAddresses?.length) {
        props.formData.clientAddresses.forEach((address: any) => {
            if (Array.isArray(address.b2bDeliveryDays) && address.b2bDeliveryDays.length) {
                address.b2bDeliveryDays = address.b2bDeliveryDays.join('| ').trim();
            } else if (typeof address.b2bDeliveryDays !== 'string') {
                address.b2bDeliveryDays = '';
            }
        });
    }

    if (props.formData.contacts?.length) {
        props.formData.contacts.forEach((contact: any) => {
            if (
                contact.address &&
                Array.isArray(contact.address.b2bDeliveryDays) &&
                contact.address.b2bDeliveryDays.length
            ) {
                contact.address.b2bDeliveryDays = contact.address.b2bDeliveryDays.join('| ').trim();
            } else if (contact.address && typeof contact.address.b2bDeliveryDays !== 'string') {
                contact.address.b2bDeliveryDays = '';
            }

            // Contacts should not update addressType, only client addresses
            if (contact.address.addressType) {
                contact.address.addressType = null;
            }
        });
    }
}

async function updateClientData(data: AvailableClientFields) {
    if (data.tags.length > 0) {
        data.clientTypes = data.tags;
    }

    data.paymentType = findPaymentTypeId(data);

    try {
        await Client.updateExisting(props.clientId, data);
        EventBus.$emit(EventBusEvents.initializeClientAttachments);
    } catch (e) {
        const error = e as ErrorOptions;
        proxy!.$notification.error({
            message: proxy!.$t('Dogodila se greška') as string,
            description: error.response ? error.response.data.meta.message : error.message,
        });

        emit('stopLoading');
        return;
    }

    return Promise.resolve();
}

async function createClientAddresses(data: AvailableClientFields) {
    if (!data.clientAddresses || data.clientAddresses.length === 0) {
        return;
    }

    const addressPromises = data.clientAddresses.map(async (address: AddressConfig) => {
        if (address.id) {
            // Update existing address
            const resp = await Address.updateExisting(address.id, address);

            mapAddressIdToContacts(address);
            return resp.data.id;
        } else {
            // Create new address and update the address object with the new ID
            const resp = await Address.createNew(address);
            address.id = resp.data.id;

            mapAddressIdToContacts(address);
            return resp.data.id;
        }
    });

    const addressIds = await Promise.all(addressPromises);

    // Set addresses array for the API
    data.addresses = addressIds;
}

function mapAddressIdToContacts(address: AddressConfig) {
    props.formData.contacts.forEach((contact: ContactConfig) => {
        if (contact.address && contact.useDefaultAddress) {
            if (address.id === contact.address?.id) {
                return;
            }

            if (!contact.address?._localId || !address?._localId) {
                return;
            }

            if (contact.address._localId === address._localId) {
                contact.address.id = address.id;
            }
        }
    });
}

async function updateContacts() {
    const contacts = [];

    try {
        for (const contact of props.formData.contacts as ContactConfig[]) {
            // Ignore empty contact form when client is person type
            if (!props.formData.isCompany && (!contact.name || !contact.email || !contact.phone)) {
                continue;
            }

            if (contact.id) {
                fixContactPersonCountryFormatting(contact);
                const addressId = contact.address?.id;

                // Handle address updates/creations for contacts that don't use company address
                if (!contact.useDefaultAddress && addressId) {
                    await Address.updateExisting(addressId, contact.address!);
                }

                if (!contact.useDefaultAddress && !addressId) {
                    const newAddress = await Address.createNew(contact.address!);
                    contact.address!.id = newAddress.data.id;
                }

                const existingContact = await ContactPerson.updateExisting(contact.id!, contact);

                contacts.push(existingContact.data.id);
            } else {
                if (!contact.useDefaultAddress) {
                    const newAddress = await Address.createNew(contact.address!);
                    contact.address!.id = newAddress.data.id;
                }

                const newContact = await ContactPerson.createNew(contact);

                contacts.push(newContact.data.id);
            }
        }
    } catch (e) {
        const error = e as ErrorOptions;
        proxy!.$notification.error({
            message: proxy!.$t('Dogodila se greška') as string,
            description: error.response ? error.response.data.meta.message : error.message,
        });

        emit('stopLoading');
        return;
    }

    return contacts;
}

function fixContactPersonCountryFormatting(contact: ContactConfig) {
    if (contact.address && contact.address.country) {
        if (typeof contact.address.country === 'string') {
            contact.address.country = contact.address.country.split('|')[0];
        } else if (contact.address.country.id) {
            contact.address.country = contact.address.country.id;
        }
    }
}

async function createDiscounts() {
    await deleteDiscounts();

    const promises: Array<Promise<any>> = [];
    props.formData.discounts.forEach((discount: DiscountTableEntry) => {
        if (discount.id) {
            promises.push(Discount.updateExisting(discount.id, discount));
        }
    });

    try {
        for (const discount of props.formData.discounts as DiscountTableEntry[]) {
            if (discount.id) {
                continue;
            }

            // Create new discounts
            discount.clientId = props.clientId;
            const resp = await Discount.createNew(discount);
            discount.id = resp.data.id;
            discount.isEditing = false;
        }

        await Promise.allSettled(promises);
    } catch (e) {
        const error = e as ErrorOptions;
        proxy!.$notification.error({
            message: proxy!.$t('Dogodila se greška') as string,
            description: error.response ? error.response.data.meta.message : error.message,
        });
        emit('stopLoading');
        return;
    }

    return Promise.resolve();
}

async function deleteDiscounts() {
    const promises: Array<Promise<any>> = [];

    deletedDiscountIds.value.forEach(async (id: string, i: number) => {
        promises.push(Discount.deleteExisting(id));
    });

    try {
        await Promise.allSettled(promises);
    } catch (e) {
        const error = e as ErrorOptions;
        proxy!.$notification.error({
            message: proxy!.$t('Dogodila se greška') as string,
            description: error.response ? error.response.data.meta.message : error.message,
        });
        emit('stopLoading');
        return;
    }

    deleteDiscountsLocally();
    return Promise.resolve();
}

function deleteDiscountsLocally() {
    props.formData.discounts = props.formData.discounts.filter(
        (discount: DiscountTableEntry) => !deletedDiscountIds.value.includes(discount.id),
    );

    deletedDiscountIds.value = [];
}

function findPaymentTypeId(data: AvailableClientFields) {
    if (!!data.paymentType) {
        return data.paymentType;
    }
    return paymentTypes.value.find((type: any) => type.isDefault)?.id;
}

function updateDeletedDiscountIds(id: string) {
    deletedDiscountIds.value.push(id);
}

async function sendToIngeniousApi() {
    if (props.clientId == null) {
        return;
    }

    try {
        await Client.sendToIngenious(props.clientId);

        proxy!.$notification.success({
            message: proxy!.$t('Klijent je uspješno poslan u Ingenious!') as string,
            description: '',
        });
    } catch (e) {
        const error = e as ErrorOptions;
        proxy!.$notification.error({
            message: proxy!.$t('Dogodila se greška') as string,
            description: error.response ? error.response.data.meta.message : error.message,
        });
        return;
    }
}

async function fetchInitialClientData() {
    try {
        await Promise.allSettled([
            Currency.getAll(),
            PaymentType.getAll(),
            ClientType.getAll(),
            BankAccount.getAll(),
            Country.getAll(),
            PriceType.getAll(),
            PriceListRegion.getAll(),
            ProductGroup.getAll(),
            CMSUser.getAllOnlyUsers(),
            LeadOrigin.getAll(),
            AddressType.getAll(),
            CapitalConnection.getAll(),
            GeneralBusinessPostingGroup.getAll(),
            CustomerPostingGroup.getAll(),
            NonResidentFinancialSector.getAll(),
            FinancialSector.getAll(),
            PaymentTerms.getAll(),
            ShipmentMethod.getAll(),
            TransactionType.getAll(),
            TransportationType.getAll(),
            ControlType.getAll(),
            DeliveryRegion.getAll(),
        ]);
    } catch (e) {
        Promise.reject(e);
    }
}

function logActiveTab(activeTabVal: any) {
    activeTab.value = activeTabVal;
}

// created logic
(async function () {
    await fetchInitialClientData();
    EventBus.$on(EventBusEvents.updateClient, updateClientAndDepenencies);
    EventBus.$on(EventBusEvents.saveClientWithDuplicates, updateClientWithDuplicatedValues);
})();

onBeforeUnmount(() => {
    EventBus.$off(EventBusEvents.updateClient, updateClientAndDepenencies);
    EventBus.$off(EventBusEvents.saveClientWithDuplicates, updateClientWithDuplicatedValues);
});

return { __sfc: true,proxy, props, emit, emailValidationOverride, emailValidationOverrideValue, isTabLoading, deletedDiscountIds, validationErrors, refErrors, isEditingDiscounts, activeTab, contactErrors, customerinfo, clientTabs, mailFormat, userRights, canUserEditDiscounts, isBasicClient, canEditAccountingInfo, proxyClientTabs, defaultCountryId, clientTypes, paymentTypes, addressTypes, capitalConnections, generalBusinessPostingGroups, customerPostingGroups, nonResidentFinancialSectors, financialSectors, paymentTerms, shipmentMethods, transactionTypes, transportationTypes, controlTypes, displayCustomerType, contactTab, isCurrentUserTechnologist, isClientProtected, setFieldsBorder, confirmFieldsAreValid, validateWebpage, checkContactFieldsForErrors, switchToCorrectTab, openDuplicateClientPopup, confirmEmailsAreValid, findFirstNonselfDuplicate, checkDuplicateEmails, isCustomerInfoShown, isContactInfoShown, isProjectsShown, isDiscountsShown, updateIsEditingDiscounts, updateCachedValues, updateClientAndDepenencies, updateClientWithDuplicatedValues, showWarningModal, formatDataForUpdate, updateClientData, createClientAddresses, mapAddressIdToContacts, updateContacts, fixContactPersonCountryFormatting, createDiscounts, deleteDiscounts, deleteDiscountsLocally, findPaymentTypeId, updateDeletedDiscountIds, sendToIngeniousApi, fetchInitialClientData, logActiveTab, ClientProjectsTableWrapper, CustomerInformation, ContactInformation, DiscountInformation, BookingInformation, PaymentsInformation, DeliveryInformation, StatusInformation, ClientNotes }
}

})